/**
 * ShopAdminPage — MADE-169 + MADE-170 + MADE-171
 *
 * Admin panel shell at /madebb/shop-admin.
 * Displays a product list with status badges, filter, and per-row Edit actions.
 *
 * API contracts:
 *   GET  /api/madebb/shop-admin/products
 *   PUT  /api/madebb/shop-admin/products/:id/price        — MADE-170
 *   PUT  /api/madebb/shop-admin/products/:id/description  — MADE-171
 *
 * Note: Bridge is building the backend endpoint concurrently. While it isn't
 * live, USE_MOCK controls whether mock data is served instead.
 *
 * All Sprint 14 follow-on stories (MADE-172–173) will add product-detail tabs
 * on top of the scaffold created here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { DiscountCodesPanel } from '../components/DiscountCodesPanel';
import { CategoriesPanel } from '../components/CategoriesPanel';
import type { ProductImage } from '../../../packages/retail-web/lib/types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** All valid product_status values per MADE-169 AC-4 */
export type ProductStatus =
  | 'draft'
  | 'active'
  | 'hidden'
  | 'discontinued'
  | 'preorder'
  | 'backorder'
  | 'coming_soon';

export interface AdminVariant {
  id: string | number;
  /** Human-readable code (e.g. "SUNBRELLA-MASTER-BLACK"). Backend returns this as `sku`; falls back to id for display. */
  sku?: string;
  name: string;
  status: string;
  current_stock: number;
  retail_price_bbd: number | null;
  parentMaterialId?: string | number;
  parent_material_id?: string | number;
}

export interface AdminProduct {
  id: string | number;
  name: string;
  sku: string;
  /** Legacy field — present in mock data; live API uses `status` */
  product_status?: ProductStatus;
  /** Live API field */
  status?: string;
  current_stock: number;
  /** Legacy mock field */
  retail_price?: number;
  /** Live API field */
  retail_price_bbd?: number | null;
  updated_at?: string;
  is_master?: boolean;
  variant_count?: number;
  variants?: AdminVariant[];
  /** MADE-272: product origin classification */
  product_type?: 'madebb_product' | 'sourced';
}

// ---------------------------------------------------------------------------
// Status badge configuration
// ---------------------------------------------------------------------------

type ChipColor = 'success' | 'default' | 'warning' | 'error' | 'info' | 'primary';

interface StatusConfig {
  label: string;
  color: ChipColor;
}

const STATUS_CONFIG: Record<ProductStatus, StatusConfig> = {
  active:       { label: 'Active',       color: 'success'  },
  draft:        { label: 'Draft',        color: 'default'  },
  hidden:       { label: 'Hidden',       color: 'warning'  },
  discontinued: { label: 'Discontinued', color: 'error'    },
  preorder:     { label: 'Preorder',     color: 'info'     },
  backorder:    { label: 'Backorder',    color: 'info'     },
  coming_soon:  { label: 'Coming Soon',  color: 'info'     },
};

/** Returns badge config for any status string; falls back to default for unknowns. */
export const getStatusConfig = (status: string): StatusConfig =>
  STATUS_CONFIG[status as ProductStatus] ?? { label: status, color: 'default' };

// ---------------------------------------------------------------------------
// ProductStatusBadge
// ---------------------------------------------------------------------------

export const ProductStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { label, color } = getStatusConfig(status);
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      data-testid={`status-badge-${status}`}
    />
  );
};

// ---------------------------------------------------------------------------
// Image Upload Component
// ---------------------------------------------------------------------------

function SortableItem(props: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({id: props.id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: '1px solid #ddd',
    padding: '8px',
    marginBottom: '8px',
    backgroundColor: 'white',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Drag handle — only the image is draggable */}
      <img src={props.image.url} alt="product" width="100" style={{ cursor: 'grab' }} {...listeners} />
      <IconButton onClick={() => props.onSetPrimary(props.image.id)} size="small">
        {props.image.is_primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
      <IconButton onClick={() => props.onDelete(props.image.id)} size="small">
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

const ImageUpload = ({ product, variantId }: { product: AdminProduct, variantId?: string | number }) => {
  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scope = variantId ? 'variant' : 'master';

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/madebb/products/${product.id}/images?t=${Date.now()}`);
      const data = await res.json();
      const allImages: ProductImage[] = data.images ?? data;
      setImages(allImages.filter((img: ProductImage) => (variantId ? img.variant_id === variantId : img.scope === 'master')));
    } catch (err) {
      setError('Failed to load images.');
    } finally {
      setLoading(false);
    }
  }, [product.id, variantId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: any) => {
    const {active, over} = event;

    if (active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);
      const newOrder = arrayMove(images, oldIndex, newIndex);
      setImages(newOrder);

      await authFetch(`/api/madebb/products/${product.id}/images/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          variant_id: variantId,
          order: newOrder.map(img => img.id),
        }),
      });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope);
    if (variantId) {
      formData.append('variant_id', String(variantId));
    }

    try {
      const res = await authFetch(`/api/madebb/products/${product.id}/images`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        fetchImages();
      }
    } catch (err) {
      setError('Failed to upload image.');
    }
  };
  
  const handleDelete = async (imageId: string) => {
    try {
      const res = await authFetch(`/api/madebb/products/${product.id}/images/${imageId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error ?? 'Failed to delete image.');
        return;
      }
      fetchImages();
    } catch (err) {
      setError('Failed to delete image.');
    }
  };
  
  const handleSetPrimary = async (imageId: string) => {
    try {
      const res = await authFetch(`/api/madebb/products/${product.id}/images/${imageId}/primary`, {
        method: 'PATCH',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error ?? 'Failed to set primary image.');
        return;
      }
      fetchImages();
    } catch (err) {
      setError('Failed to set primary image.');
    }
  };

  return (
    <Box>
      <input type="file" onChange={handleImageUpload} />
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <CircularProgress />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={images}
          strategy={verticalListSortingStrategy}
        >
          {images.map(image => (
            <SortableItem key={image.id} id={image.id} image={image} onDelete={handleDelete} onSetPrimary={handleSetPrimary} />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};


export const MOCK_PRODUCTS: AdminProduct[] = [
  {
    id: 1,
    name: 'Black PLA Filament',
    sku: 'FIL-BLK-001',
    product_status: 'active',
    current_stock: 15,
    retail_price: 35.00,
    updated_at: new Date().toISOString(),
    is_master: false,
    variant_count: 0,
    variants: [],
    product_type: 'madebb_product' as const,
  },
  {
    id: 2,
    name: 'White PLA Filament',
    sku: 'FIL-WHT-002',
    product_status: 'active',
    current_stock: 8,
    retail_price: 35.00,
    updated_at: new Date().toISOString(),
    is_master: true,
    variant_count: 2,
    variants: [
      { id: 'FIL-WHT-002-SM', name: 'Small', status: 'active', current_stock: 4, retail_price_bbd: 35.00 },
      { id: 'FIL-WHT-002-LG', name: 'Large', status: 'active', current_stock: 4, retail_price_bbd: 40.00 },
    ],
  },
  {
    id: 3,
    name: 'Custom Print - Small',
    sku: 'PRINT-SM-001',
    product_status: 'draft',
    current_stock: 0,
    retail_price: 25.00,
    updated_at: new Date().toISOString(),
    is_master: false,
    variant_count: 0,
    variants: [],
  },
  {
    id: 4,
    name: 'Roll Shade 6x4',
    sku: 'SHADE-6X4-001',
    product_status: 'hidden',
    current_stock: 5,
    retail_price: 120.00,
    updated_at: new Date().toISOString(),
    is_master: false,
    variant_count: 0,
    variants: [],
  },
  {
    id: 5,
    name: 'Legacy Item',
    sku: 'LEGACY-001',
    product_status: 'discontinued',
    current_stock: 0,
    retail_price: 0.00,
    updated_at: new Date().toISOString(),
    is_master: false,
    variant_count: 0,
    variants: [],
  },
];

// ---------------------------------------------------------------------------
// Feature flag — flip to false when Bridge endpoint is live
// ---------------------------------------------------------------------------
const USE_MOCK = false;
const PRODUCTS_API = '/api/madebb/shop-admin/products';

// ---------------------------------------------------------------------------
// EditProductDrawer — MADE-170 (pricing) + MADE-171 (description)
// ---------------------------------------------------------------------------

export interface EditProductDrawerProps {
  product: AdminProduct | null;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const EditProductDrawer: React.FC<EditProductDrawerProps> = ({
  product,
  open,
  onClose,
  onRefresh,
}) => {
  const [priceValue, setPriceValue]       = useState<string>('');
  const [descValue, setDescValue]         = useState<string>('');
  const [categories, setCategories]       = useState<Array<{id: number; name: string; slug: string}>>([]);
  // MADE-265: multi-select — array of category IDs (as strings for consistent key handling)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  // MADE-272: product type classification
  const [productType, setProductType] = useState<'madebb_product' | 'sourced'>('madebb_product');
  const [productTypeSaving, setProductTypeSaving] = useState(false);
  const [productTypeError, setProductTypeError] = useState<string | null>(null);
  const [productTypeSuccess, setProductTypeSuccess] = useState(false);
  const [priceSaving, setPriceSaving]     = useState(false);
  const [descSaving, setDescSaving]       = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [priceError, setPriceError]       = useState<string | null>(null);
  const [descError, setDescError]         = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [priceSuccess, setPriceSuccess]   = useState(false);
  const [descSuccess, setDescSuccess]     = useState(false);
  const [categorySuccess, setCategorySuccess] = useState(false);
  const [seoSuccess, setSeoSuccess]       = useState(false);
  // Track last product id to only reset desc when switching products
  const prevProductIdRef = React.useRef<string | number | null>(null);

  // MADE-176: SEO states
  const [slug, setSlug] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [seoSaving, setSeoSaving] = useState(false);
  const [seoError, setSeoError] = useState<string | null>(null);

  // MADE-179: Image states
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // MADE-196: Variant inherited values
  const [inheritedPrice, setInheritedPrice] = useState<number | null>(null);
  const [inheritedDescription, setInheritedDescription] = useState<string | null>(null);

  // Reset fields and fetch categories whenever the selected product changes
  useEffect(() => {
    if (product && open) {
      const p = product.retail_price_bbd ?? product.retail_price ?? 0;
      setPriceValue(p > 0 ? p.toFixed(2) : '');
      // MADE-265: reset multi-select; actual values loaded by fetchProductCategories below
      setSelectedCategoryIds([]);
      // MADE-272: reset product type
      setProductType((product.product_type as 'madebb_product' | 'sourced') || 'madebb_product');
      setProductTypeError(null);
      setProductTypeSuccess(false);
      setPriceError(null);
      setDescError(null);
      setCategoryError(null);
      setPriceSuccess(false);
      setDescSuccess(false);
      setCategorySuccess(false);
      setSeoSuccess(false);

      const productChanged = prevProductIdRef.current !== product.id;
      if (productChanged) {
        // Reset description immediately; will be overwritten by fetch below
        setDescValue('');
        prevProductIdRef.current = product.id;
      }

      // Fetch categories
      const fetchCategories = async () => {
        try {
          const res = await authFetch('/api/madebb/shop-admin/categories');
          if (!res.ok) throw new Error('Could not load categories');
          const data = await res.json();
          setCategories(data.categories ?? []);
        } catch (err: unknown) {
          setCategoryError(
            err instanceof Error ? err.message : 'Failed to load categories',
          );
        }
      };
      fetchCategories();

      // MADE-265: Fetch this product's current category assignments
      const fetchProductCategories = async () => {
        try {
          const res = await authFetch(`/api/madebb/shop-admin/products/${product.id}/categories`);
          if (!res.ok) throw new Error('Could not load product categories');
          const data = await res.json();
          setSelectedCategoryIds((data.categories ?? []).map((c: { id: number }) => String(c.id)));
        } catch (_err: unknown) {
          // Non-fatal: if this fails, selection starts empty; user can re-assign
        }
      };
      fetchProductCategories();

      // MADE-176: Fetch SEO data
      const fetchSeo = async () => {
        try {
          const res = await authFetch(`/api/madebb/shop-admin/products/${product.id}/seo`);
          if (!res.ok) {
            // It's okay if it's a 404, just means no SEO data yet
            if (res.status === 404) {
                const generatedSlug = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                setSlug(generatedSlug);
                setMetaTitle(product.name);
                setMetaDescription('');
                return;
            }
            throw new Error('Could not load SEO data');
          }
          const data = await res.json();
          setSlug(data.slug ?? '');
          setMetaTitle(data.meta_title ?? '');
          setMetaDescription(data.meta_description ?? '');
        } catch (err: unknown) {
          setSeoError(
            err instanceof Error ? err.message : 'Failed to load SEO data',
          );
        }
      };
      fetchSeo();

      // Fetch full product details to populate description and inherited/variant fields.
      // This runs for ALL products (not just variants) so that:
      //   - Master/standalone products get their description (notes) pre-populated
      //   - Variants get variant_description and variant_retail_price pre-populated
      //   - Inherited price/description helper text is correctly sourced from data.product
      const isVariant = !!(product.parentMaterialId || product.parent_material_id);
      setInheritedPrice(null);
      setInheritedDescription(null);
      authFetch(`/api/madebb/shop-admin/products/${product.id}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (!data?.product) return;
          const prd = data.product;
          // Populate description: variant override first, then master notes
          const savedDesc = isVariant
            ? (prd.variant_description ?? '')
            : (prd.notes ?? '');
          setDescValue(savedDesc);
          // For variants: populate price override if one exists
          if (isVariant && prd.variant_retail_price != null) {
            setPriceValue(prd.variant_retail_price.toFixed(2));
          }
          // Inherited values live on data.product (not data)
          setInheritedPrice(prd.inherited_price ?? null);
          setInheritedDescription(prd.inherited_description ?? null);
        })
        .catch(() => {});

      // MADE-179: Fetch image
      setImageUrl(null);
      setImageError(null);
      authFetch(`/api/madebb/shop-admin/products/${product.id}/image`)
        .then(res => res.ok ? res.json() : { url: null })
        .then(data => setImageUrl(data.url ?? null))
        .catch(() => setImageUrl(null));
    }
  }, [product, open]);

  // MADE-179: Image upload handler
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!product) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);
    if (file.size > 5 * 1024 * 1024) {
      setImageError('File must be under 5MB');
      return;
    }
    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await authFetch(`/api/madebb/shop-admin/products/${product.id}/image`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setImageUrl(data.url ?? null);
    } catch (err: unknown) {
      setImageError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setImageLoading(false);
    }
  };

  // MADE-179: Image remove handler
  const handleRemoveImage = async () => {
    if (!product) return;
    setImageLoading(true);
    setImageError(null);
    try {
      await authFetch(`/api/madebb/shop-admin/products/${product.id}/image`, { method: 'DELETE' });
      setImageUrl(null);
    } catch (err: unknown) {
      setImageError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setImageLoading(false);
    }
  };

  const handleSavePrice = async () => {
    if (!product) return;
    setPriceSaving(true);
    setPriceError(null);
    try {
      const res = await authFetch(
        `/api/madebb/shop-admin/products/${product.id}/price`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ retail_price_bbd: parseFloat(priceValue) }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message ?? `Server returned ${res.status}`,
        );
      }
      onRefresh();
      setPriceSuccess(true);
      setTimeout(() => setPriceSuccess(false), 3000);
    } catch (err: unknown) {
      setPriceError(
        err instanceof Error ? err.message : 'Failed to save price',
      );
    } finally {
      setPriceSaving(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!product) return;
    setDescSaving(true);
    setDescError(null);
    try {
      const res = await authFetch(
        `/api/madebb/shop-admin/products/${product.id}/description`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description: descValue }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message ?? `Server returned ${res.status}`,
        );
      }
      onRefresh();
      setDescSuccess(true);
      setTimeout(() => setDescSuccess(false), 3000);
    } catch (err: unknown) {
      setDescError(
        err instanceof Error ? err.message : 'Failed to save description',
      );
    } finally {
      setDescSaving(false);
    }
  };

  // MADE-265: save multi-category assignment via PUT /categories
  const handleSaveCategory = async () => {
    if (!product) return;
    setCategorySaving(true);
    setCategoryError(null);
    try {
      const categoryIds = selectedCategoryIds.map(id => parseInt(id, 10));
      const res = await authFetch(
        `/api/madebb/shop-admin/products/${product.id}/categories`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryIds }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message ?? `Server returned ${res.status}`,
        );
      }
      onRefresh();
      setCategorySuccess(true);
      setTimeout(() => setCategorySuccess(false), 3000);
    } catch (err: unknown) {
      setCategoryError(
        err instanceof Error ? err.message : 'Failed to save category',
      );
    } finally {
      setCategorySaving(false);
    }
  };

  // MADE-272: Save product type
  const handleSaveProductType = async () => {
    if (!product) return;
    setProductTypeSaving(true);
    setProductTypeError(null);
    try {
      const res = await authFetch(
        `/api/madebb/shop-admin/products/${product.id}/product-type`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_type: productType }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? `Server returned ${res.status}`);
      }
      onRefresh();
      setProductTypeSuccess(true);
      setTimeout(() => setProductTypeSuccess(false), 3000);
    } catch (err: unknown) {
      setProductTypeError(err instanceof Error ? err.message : 'Failed to save product type');
    } finally {
      setProductTypeSaving(false);
    }
  };

  // MADE-176: Save SEO
  const handleSaveSeo = async () => {
    if (!product) return;
    setSeoSaving(true);
    setSeoError(null);
    try {
      const res = await authFetch(
        `/api/madebb/shop-admin/products/${product.id}/seo`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, meta_title: metaTitle, meta_description: metaDescription }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { message?: string }).message ?? `Server returned ${res.status}`,
        );
      }
      onRefresh();
      setSeoSuccess(true);
      setTimeout(() => setSeoSuccess(false), 3000);
    } catch (err: unknown) {
      setSeoError(
        err instanceof Error ? err.message : 'Failed to save SEO',
      );
    } finally {
      setSeoSaving(false);
    }
  };

  const MAX_DESC = 2000;
  const MAX_META_TITLE = 60;
  const MAX_META_DESC = 160;

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const isVariant = !!(product?.parentMaterialId || product?.parent_material_id);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 }, 'data-testid': isVariant ? 'variant-drawer' : 'edit-product-drawer' }}
    >
      {product && (
        <Box data-testid={isVariant ? 'variant-drawer' : 'edit-price-drawer'}>
          {/* ── Header ────────────────────────────────────────────── */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 3 }}
            data-testid="drawer-header"
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Edit Product
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="drawer-product-name">
                {product.name}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="Close drawer">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Tabs value={tabValue} onChange={handleTabChange} aria-label="product edit tabs">
            <Tab label="Details" />
            <Tab label="Images" />
          </Tabs>

          {tabValue === 0 && (
            <Box>
              {/* ── Pricing — MADE-170 ────────────────────────────────── */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Pricing
              </Typography>

              <TextField
                label="Retail Price"
                type="number"
                value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)}
                inputProps={{
                  step: '0.01',
                  min: '0',
                  'data-testid': isVariant ? 'variant-price-input' : 'price-input',
                }}
                helperText={
                  isVariant && inheritedPrice != null && !priceValue
                    ? <span data-testid="inherited-price-label">Inherits from master: ${inheritedPrice.toFixed(2)}</span>
                    : undefined
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">BBD $</InputAdornment>
                  ),
                }}
                fullWidth
                size="small"
                sx={{ mb: 1 }}
              />

              {priceError && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="price-error-alert">
                  {priceError}
                </Alert>
              )}
              {priceSuccess && (
                <Alert severity="success" sx={{ mb: 1 }} data-testid="price-success-alert">
                  Price saved ✓
                </Alert>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleSavePrice}
                disabled={priceSaving}
                data-testid="save-price-btn"
                sx={{ mb: 3 }}
              >
                {priceSaving ? 'Saving…' : 'Save Price'}
              </Button>

              <Divider sx={{ mb: 3 }} />

              {/* ── Description — MADE-171 ────────────────────────────── */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Description
              </Typography>

              <TextField
                label="Description"
                multiline
                rows={6}
                value={descValue}
                onChange={(e) =>
                  setDescValue(e.target.value.slice(0, MAX_DESC))
                }
                inputProps={{
                  maxLength: MAX_DESC,
                  'data-testid': 'description-input',
                }}
                helperText={
                  isVariant && inheritedDescription && !descValue
                    ? `Inherits from master: ${inheritedDescription}`
                    : `${descValue.length} / ${MAX_DESC} characters`
                }
                fullWidth
                size="small"
                sx={{ mb: 1 }}
              />

              {descError && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="desc-error-alert">
                  {descError}
                </Alert>
              )}
              {descSuccess && (
                <Alert severity="success" sx={{ mb: 1 }} data-testid="desc-success-alert">
                  Description saved ✓
                </Alert>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleSaveDescription}
                disabled={descSaving}
                data-testid="save-description-btn"
                sx={{ mb: 3 }}
              >
                {descSaving ? 'Saving…' : 'Save Description'}
              </Button>

              <Divider sx={{ mb: 3 }} />

              {/* ── Categories — MADE-265 multi-assign ───────────────── */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Categories
              </Typography>

              {/* Selected categories as chips */}
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 36,
                      border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}
                data-testid="selected-categories"
              >
                {selectedCategoryIds.length === 0 ? (
                  <Typography variant="caption" color="text.secondary"
                    sx={{ alignSelf: 'center', pl: 0.5 }}>
                    No categories — add one
                  </Typography>
                ) : (
                  selectedCategoryIds.map(id => {
                    const cat = categories.find(c => String(c.id) === id);
                    return cat ? (
                      <Chip
                        key={id}
                        label={cat.name}
                        size="small"
                        onDelete={() =>
                          setSelectedCategoryIds(ids => ids.filter(x => x !== id))
                        }
                        data-testid={`category-chip-${id}`}
                      />
                    ) : null;
                  })
                )}
              </Box>

              {/* Dropdown to add a category (shows only unassigned ones) */}
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (val && !selectedCategoryIds.includes(val)) {
                      setSelectedCategoryIds(ids => [...ids, val]);
                    }
                  }}
                  data-testid="category-select"
                >
                  <MenuItem value="" disabled>
                    <em>— Add a category —</em>
                  </MenuItem>
                  {categories
                    .filter(cat => !selectedCategoryIds.includes(String(cat.id)))
                    .map(cat => (
                      <MenuItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {categoryError && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="category-error-alert">
                  {categoryError}
                </Alert>
              )}
              {categorySuccess && (
                <Alert severity="success" sx={{ mb: 1 }} data-testid="category-success-alert">
                  Category saved ✓
                </Alert>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleSaveCategory}
                disabled={categorySaving}
                data-testid="save-category-btn"
              >
                {categorySaving ? 'Saving…' : 'Save Category'}
              </Button>

              <Divider sx={{ mb: 3 }} />

              {/* ── Product Type — MADE-272 ─────────────────────────────── */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Product Type
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <Select
                  value={productType}
                  onChange={(e) => setProductType(e.target.value as 'madebb_product' | 'sourced')}
                  data-testid="product-type-select"
                >
                  <MenuItem value="madebb_product">🏻 Made.bb Product</MenuItem>
                  <MenuItem value="sourced">📦 Sourced</MenuItem>
                </Select>
              </FormControl>

              {productTypeError && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="product-type-error-alert">
                  {productTypeError}
                </Alert>
              )}
              {productTypeSuccess && (
                <Alert severity="success" sx={{ mb: 1 }} data-testid="product-type-success-alert">
                  Product type saved ✓
                </Alert>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleSaveProductType}
                disabled={productTypeSaving}
                data-testid="save-product-type-btn"
              >
                {productTypeSaving ? 'Saving…' : 'Save Product Type'}
              </Button>

              <Divider sx={{ mb: 3 }} />

              {/* ── SEO — MADE-176 ────────────────────────────────────── */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                SEO
              </Typography>

              <TextField
                label="URL Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                inputProps={{ 'data-testid': 'seo-slug-input' }}
              />

              <TextField
                label="Meta Title"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value.slice(0, MAX_META_TITLE))}
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                inputProps={{ maxLength: MAX_META_TITLE, 'data-testid': 'seo-meta-title-input' }}
                helperText={`${metaTitle.length} / ${MAX_META_TITLE} characters`}
              />

              <TextField
                label="Meta Description"
                multiline
                rows={4}
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value.slice(0, MAX_META_DESC))}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                inputProps={{ maxLength: MAX_META_DESC, 'data-testid': 'seo-meta-description-input' }}
                helperText={`${metaDescription.length} / ${MAX_META_DESC} characters`}
              />

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }} data-testid="seo-preview-box">
                <Typography sx={{ color: '#1a0dab', fontSize: '1.25rem' }}>
                  {metaTitle || product.name}
                </Typography>
                <Typography sx={{ color: '#006621', fontSize: '0.875rem' }}>
                  https://made.bb/shop/{slug}
                </Typography>
                <Typography sx={{ color: '#545454', fontSize: '0.875rem' }}>
                  {metaDescription || 'No meta description provided.'}
                </Typography>
              </Box>


              {seoError && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="seo-error-alert">
                  {seoError}
                </Alert>
              )}
              {seoSuccess && (
                <Alert severity="success" sx={{ mb: 1 }} data-testid="seo-success-alert">
                  SEO saved ✓
                </Alert>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleSaveSeo}
                disabled={seoSaving}
                data-testid="save-seo-btn"
              >
                {seoSaving ? 'Saving…' : 'Save SEO'}
              </Button>
            </Box>
          )}
          {tabValue === 1 && (
            <Box>
              {product.is_master && product.variants && product.variants.length > 0 ? (
                // Master product: show image upload per variant
                <Box>
                  {/* MADE-271: Generic / master-scope images at top of Images tab */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
                    data-testid="generic-images-section">
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Generic / All colours
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                      Shown as fallback when a colour variant has no specific image.
                    </Typography>
                    <ImageUpload product={product} />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Images are managed per variant. Upload images for each colour/option below.
                  </Typography>
                  {product.variants.map((v) => (
                    <Box key={v.id} sx={{ mb: 3, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {v.name || v.label || v.id}
                      </Typography>
                      <ImageUpload product={product} variantId={v.sku ?? v.id} />
                    </Box>
                  ))}
                </Box>
              ) : product.parentMaterialId ? (
                // Variant product: upload images scoped to this variant (use sku = string material id)
                <ImageUpload product={{ ...product, id: product.parentMaterialId }} variantId={product.sku ?? product.id} />
              ) : (
                // Standalone product: upload master-scoped images
                <ImageUpload product={product} />
              )}
            </Box>
          )}

        </Box>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// ShopAdminPage
// ---------------------------------------------------------------------------

export const ShopAdminPage: React.FC = () => {
  const navigate = useNavigate();

  // Fix 6: Top-level tab — 0 = Products, 1 = Discount Codes, 2 = Categories
  const [adminTab, setAdminTab] = useState(0);

  const [products, setProducts]             = useState<AdminProduct[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [statusFilter, setStatusFilter]     = useState<string>('all');
  const [expandedIds, setExpandedIds]       = useState<Set<string | number>>(new Set());
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null);
  const [drawerOpen, setDrawerOpen]         = useState(false);
  // MADE-268: search + filter states
  const [searchQ, setSearchQ]               = useState('');
  const [debouncedQ, setDebouncedQ]         = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterMinPrice, setFilterMinPrice] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  const [filterProductType, setFilterProductType] = useState('');
  const [filterStockStatus, setFilterStockStatus] = useState('');
  const [filterShopVisible, setFilterShopVisible] = useState('');
  const [totalAll, setTotalAll]             = useState(0);
  // Available categories for the filter dropdown
  const [adminCategories, setAdminCategories] = useState<Array<{id: number; name: string; slug: string}>>([]);

  // Debounce text search by 300 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const hasActiveFilters = !!(debouncedQ || filterCategory || filterMinPrice || filterMaxPrice ||
    filterProductType || filterStockStatus || filterShopVisible);

  const clearFilters = () => {
    setSearchQ('');
    setDebouncedQ('');
    setFilterCategory('');
    setFilterMinPrice('');
    setFilterMaxPrice('');
    setFilterProductType('');
    setFilterStockStatus('');
    setFilterShopVisible('');
  };

  const handleEditClick = (product: AdminProduct) => {
    setEditingProduct(product);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const toggleExpand = (id: string | number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (USE_MOCK) {
        setProducts(MOCK_PRODUCTS);
        setTotalAll(MOCK_PRODUCTS.length);
      } else {
        const params = new URLSearchParams();
        if (debouncedQ)          params.set('q', debouncedQ);
        if (filterCategory)      params.set('category', filterCategory);
        if (filterMinPrice)      params.set('minPrice', filterMinPrice);
        if (filterMaxPrice)      params.set('maxPrice', filterMaxPrice);
        if (filterProductType)   params.set('productType', filterProductType);
        if (filterStockStatus)   params.set('stockStatus', filterStockStatus);
        if (filterShopVisible)   params.set('shopVisible', filterShopVisible);
        const url = `${PRODUCTS_API}${params.size ? '?' + params.toString() : ''}`;
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data = await res.json();
        setProducts(data.products ?? data);
        if (data.totalAll != null) setTotalAll(data.totalAll);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load products';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, filterCategory, filterMinPrice, filterMaxPrice, filterProductType, filterStockStatus, filterShopVisible]);

  // Fetch products whenever filters change (debouncedQ handles text debounce)
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch available categories for the filter dropdown
  useEffect(() => {
    authFetch('/api/madebb/shop-admin/categories')
      .then(r => r.ok ? r.json() : { categories: [] })
      .then(d => setAdminCategories(d.categories ?? []))
      .catch(() => {});
  }, []);

  const filteredProducts =
    statusFilter === 'all'
      ? products
      : products.filter((p) => (p.product_status ?? p.status) === statusFilter);

  return (
    <Box data-testid="shop-admin-page">
      {/* Breadcrumb */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <RouterLink to="/madebb/inventory" style={{ textDecoration: 'none', color: 'inherit' }}>
          Made.bb
        </RouterLink>
        <Typography color="text.primary">Shop Admin</Typography>
      </Breadcrumbs>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        Shop Admin
      </Typography>

      {/* MADE-252: Top-level navigation tabs */}
      <Tabs
        value={adminTab}
        onChange={(_e, v) => setAdminTab(v)}
        aria-label="shop admin sections"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        data-testid="shop-admin-tabs"
      >
        <Tab label="Products" data-testid="tab-products" />
        <Tab label="Discount Codes" data-testid="tab-discount-codes" />
        <Tab label="Categories" data-testid="tab-categories" />
      </Tabs>

      {/* ── Discount Codes tab — MADE-252 ─────────────────────── */}
      {adminTab === 1 && <DiscountCodesPanel />}

      {/* ── Categories tab — Fix 6 ───────────────────────────── */}
      {adminTab === 2 && <CategoriesPanel />}

      {/* ── Products tab ─────────────────────────────────────── */}
      {adminTab === 0 && <>

      {/* MADE-268: Search + filter bar ──────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }} data-testid="shop-admin-filter-bar">
        <Stack spacing={1.5}>
          {/* Row 1: text search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search products by name, SKU, or description…"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            inputProps={{ 'data-testid': 'shop-admin-search-input' }}
            InputProps={{
              startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box>,
            }}
          />
          {/* Row 2: attribute filters */}
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            {/* Category */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={filterCategory}
                displayEmpty
                onChange={e => setFilterCategory(e.target.value as string)}
                data-testid="filter-category"
              >
                <MenuItem value=""><em>All categories</em></MenuItem>
                {adminCategories.map(cat => (
                  <MenuItem key={cat.id} value={cat.slug}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Price range */}
            <TextField
              size="small" type="number" placeholder="Min $"
              value={filterMinPrice}
              onChange={e => setFilterMinPrice(e.target.value)}
              inputProps={{ 'data-testid': 'filter-min-price', min: 0 }}
              sx={{ width: 90 }}
            />
            <TextField
              size="small" type="number" placeholder="Max $"
              value={filterMaxPrice}
              onChange={e => setFilterMaxPrice(e.target.value)}
              inputProps={{ 'data-testid': 'filter-max-price', min: 0 }}
              sx={{ width: 90 }}
            />

            {/* Product type */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={filterProductType}
                displayEmpty
                onChange={e => setFilterProductType(e.target.value as string)}
                data-testid="filter-product-type"
              >
                <MenuItem value=""><em>All types</em></MenuItem>
                <MenuItem value="madebb_product">🏻 Made.bb Product</MenuItem>
                <MenuItem value="sourced">📦 Sourced</MenuItem>
              </Select>
            </FormControl>

            {/* Stock status */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={filterStockStatus}
                displayEmpty
                onChange={e => setFilterStockStatus(e.target.value as string)}
                data-testid="filter-stock-status"
              >
                <MenuItem value=""><em>All stock</em></MenuItem>
                <MenuItem value="in_stock">In stock</MenuItem>
                <MenuItem value="low_stock">Low stock</MenuItem>
                <MenuItem value="out_of_stock">Out of stock</MenuItem>
              </Select>
            </FormControl>

            {/* Shop visible */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={filterShopVisible}
                displayEmpty
                onChange={e => setFilterShopVisible(e.target.value as string)}
                data-testid="filter-shop-visible"
              >
                <MenuItem value=""><em>All visibility</em></MenuItem>
                <MenuItem value="true">Shop visible</MenuItem>
                <MenuItem value="false">Hidden</MenuItem>
              </Select>
            </FormControl>

            {/* Clear button */}
            {hasActiveFilters && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={clearFilters}
                data-testid="clear-filters-btn"
              >
                Clear filters
              </Button>
            )}
          </Stack>

          {/* Results count */}
          <Typography variant="caption" color="text.secondary" data-testid="results-count">
            {hasActiveFilters
              ? `Showing ${filteredProducts.length} of ${totalAll} products`
              : `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}`
            }
          </Typography>
        </Stack>
      </Paper>

      {/* Toolbar */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as string)}
            inputProps={{ 'data-testid': 'status-filter' }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            {(Object.entries(STATUS_CONFIG) as [ProductStatus, StatusConfig][]).map(
              ([value, cfg]) => (
                <MenuItem key={value} value={value}>
                  {cfg.label}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchProducts}
          disabled={loading}
          data-testid="refresh-button"
        >
          Refresh
        </Button>

        {loading && <CircularProgress size={20} data-testid="loading-spinner" />}
      </Stack>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="error-alert">
          {error}
        </Alert>
      )}

      {/* Product table */}
      <Paper>
        <TableContainer>
          <Table data-testid="product-table" aria-label="product list">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Price (BBD)</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.length === 0 && !loading ? (
                <TableRow data-testid="empty-row">
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No products found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.flatMap((product) => {
                  const productStatus = product.product_status ?? product.status ?? 'draft';
                  const price = product.retail_price_bbd ?? product.retail_price ?? 0;
                  const hasVariants = (product.variant_count ?? 0) > 0;
                  const isExpanded = expandedIds.has(product.id);

                  const masterRow = (
                    <TableRow
                      key={product.id}
                      hover
                      data-testid={`product-row-${product.id}`}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {product.name}
                          {hasVariants && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 0, p: 0.5, fontSize: '0.75rem', lineHeight: 1 }}
                              onClick={() => toggleExpand(product.id)}
                              data-testid={`variant-count-${product.id}`}
                            >
                              {isExpanded ? '▼' : '▶'} {product.variant_count} variant{product.variant_count !== 1 ? 's' : ''}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{product.sku}</TableCell>
                      <TableCell>
                        <ProductStatusBadge status={productStatus} />
                      </TableCell>
                      {/* MADE-272: product type badge */}
                      <TableCell>
                        <Chip
                          label={product.product_type === 'sourced' ? '📦 Sourced' : '🏻 Made.bb'}
                          size="small"
                          variant="outlined"
                          data-testid={`product-type-badge-${product.id}`}
                        />
                      </TableCell>
                      <TableCell align="right">{product.current_stock}</TableCell>
                      <TableCell align="right">
                        {price > 0 ? `$${price.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEditClick(product)}
                          data-testid={`edit-btn-${product.id}`}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );

                  const variantRows =
                    isExpanded && product.variants
                      ? product.variants.map((v) => (
                          <TableRow
                            key={v.id}
                            data-testid={`variant-row-${v.id}`}
                            sx={{ backgroundColor: 'action.hover' }}
                          >
                            <TableCell sx={{ pl: 5 }}>
                              <Typography variant="body2" color="text.secondary">
                                ↳ {v.name}
                              </Typography>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                                {v.sku ?? v.id}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                              {v.sku ?? v.id}
                            </TableCell>
                            <TableCell>
                              <ProductStatusBadge status={v.status} />
                            </TableCell>
                            <TableCell align="right">{v.current_stock}</TableCell>
                            <TableCell align="right">
                              {v.retail_price_bbd != null && v.retail_price_bbd > 0
                                ? `$${v.retail_price_bbd.toFixed(2)}`
                                : '—'}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                data-testid="edit-variant-btn"
                                onClick={() => handleEditClick({
                                  id: v.id,
                                  name: v.name,
                                  sku: v.sku ?? String(v.id),
                                  status: v.status,
                                  current_stock: v.current_stock,
                                  retail_price_bbd: v.retail_price_bbd,
                                  parentMaterialId: product.id,
                                })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      : [];

                  return [masterRow, ...variantRows];
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit drawer — MADE-170 + MADE-171 */}
      <EditProductDrawer
        product={editingProduct}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onRefresh={fetchProducts}
      />

      </> /* end Products tab */}
    </Box>
  );
};
